import {
  Grid,
  GridItem,
  HStack,
  IconButton,
  Text,
  Box,
  VStack
} from '@chakra-ui/react';
import React, { FC } from 'react';
import { LogoSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/LogoSection';
import { SymbolsSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/SymbolsSection';
import { StrategySection } from '@app/components/StrategySection';
import { PropertiesSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/PropertiesSection';
import { SellPrice } from '@app/screens/nft-marketplace/SellPrice';
import { Icon } from '@app/components/Icon';
import { NetworkIcon } from '@app/components/NetworkIcon';
import { getNetworkIconUrl } from '@app/screens/positions/components/MarketMakerWidget/helpers';
import { Mode } from '@thirdweb-dev/chains';
import { TokenIcon } from '@app/components/TokenIcon';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { Token } from '@app/types/token';
import { usePositionKpNftDetailedData } from '@app/hooks/positions/usePositionKpNftDetailedData';
import { useIsStakedToNitro } from '@app/hooks/nitro/useIsStakedToNitro';
import { queryClient } from '@app/context/AppContextWrapper';
import { QueryKeys } from '@app/constants/queryKeys';
import { PairToken } from '@app/types/pool';
import { DetailsSection } from '@app/screens/nft-marketplace/MakeOfferDialog/components/DetailsSection';
import { getAuctionDescription } from '@app/helpers/nft/getAuctionDescription';
import { formatUnits } from 'ethers/lib/utils';
import { formatNumber } from '@app/helpers/format';
import { useGetBestBidForListing } from '@app/hooks/nft/useGetBestBidForListing';
import { useTokenBalance } from '@app/hooks/token/useTokenBalance';
import { useAddress } from '@thirdweb-dev/react';
import { useDiscountValue } from '@app/hooks/nft/useDiscountValue';
import { useToggle } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';
import { RxHamburgerMenu } from 'react-icons/rx';
import { NftCartItemAnimatedContainer } from '@app/screens/nft-marketplace/Cart/NftCartItemAnimatedContainer';
import { RemoveItemButton } from '@app/screens/nft-marketplace/Cart/RemoveItemButton';
import { useFindTokenInWhitelist } from '@app/hooks/nft/useFindTokenInWhitelist';

type Prop = {
  isMobile: boolean;
  listing: NFTMarketplaceListing;
};

export const NftCartItem: FC<Prop> = ({ isMobile, listing }) => {
  const [openDetails, toggleOpen] = useToggle(false);
  const address = useAddress();
  const { id, tokenId, nftPool, isNFTAvailable, nftMetadata } = listing;

  const { vault, lockDetails, usdAmount, data } = usePositionKpNftDetailedData(
    nftPool.id,
    tokenId,
    nftPool.lpToken as PairToken
  );

  const isStakedToNitro = useIsStakedToNitro(nftPool.id, tokenId);

  const token = useFindTokenInWhitelist(listing);

  const { data: bestBidForListing } = useGetBestBidForListing(listing.id);

  const { balance } = useTokenBalance(token!.contractAddress, address ?? '');

  const discount = useDiscountValue(listing, token as Token, usdAmount);

  const formatBestOffer = () => {
    return bestBidForListing?.amount && token
      ? `${formatUnits(bestBidForListing?.amount, token?.decimals)} ${
          token.symbol
        }`
      : '-';
  };

  const formatBalance = () => {
    return balance ? `${formatNumber(+balance, 3)} ${token!.symbol}` : '-';
  };

  const formatReservePrice = () => {
    return listing.reserve && token
      ? `${formatUnits(listing.reserve, token?.decimals)} ${token.symbol}`
      : '-';
  };

  const renderDetails = () => {
    return (
      <NftCartItemAnimatedContainer>
        <GridItem width="100%" padding="16px">
          <DetailsSection
            auctionType={listing.type}
            auctionDescription={getAuctionDescription(listing.type)}
            bestOffer={formatBestOffer()}
            balance={formatBalance()}
            reservePrice={formatReservePrice()}
            discount={discount}
            deposit={data?.amount}
            depositUsd={usdAmount}
          />
          {isMobile ? (
            <HStack width="100%" justifyContent="space-between">
              <PropertiesSection
                lockDetails={lockDetails}
                poolAddress={nftPool.id}
                tokenId={tokenId}
                isStakedToNitro={isStakedToNitro}
              />
              <HStack gap="8px">
                <NetworkIcon
                  w="32px"
                  h="32px"
                  name={Mode.name}
                  iconUrl={getNetworkIconUrl(Mode.chainId).replace(
                    'ipfs://',
                    'https://ipfs.io/ipfs/'
                  )}
                  imageSize={{ width: '32px', height: '32px' }}
                />
                <TokenIcon symbol="Kim" width="32px" height="32px" />
              </HStack>
              <RemoveItemButton id={listing.id} />
            </HStack>
          ) : null}
        </GridItem>
      </NftCartItemAnimatedContainer>
    );
  };

  return (
    <>
      {isMobile ? (
        <Grid
          width="100%"
          borderRadius="8px"
          border="1px solid"
          borderColor={isNFTAvailable ? 'gray.15' : 'error'}
          backgroundColor="black"
          justifyItems="center"
          padding="8px 16px"
          templateColumns="repeat(auto-fit, minmax(50px, 1fr))"
          alignItems="center"
          gap={1}
        >
          <GridItem>
            <LogoSection vault={vault} tokenDetails={nftPool?.lpToken} />
          </GridItem>
          <GridItem>
            <VStack alignItems="flex-start" gap={1}>
              <SymbolsSection
                fontSize="10px"
                vault={vault}
                tokenDetails={nftPool?.lpToken}
              />
              <Text fontSize="10px" color="gray.4">
                ID: {tokenId}
              </Text>
            </VStack>
          </GridItem>
          <GridItem>
            <StrategySection fontSize="8xp" vault={vault} />
          </GridItem>
          <GridItem justifySelf="flex-start">
            {isNFTAvailable && token ? (
              <SellPrice listing={listing} currency={token} />
            ) : (
              <Text
                color="error"
                fontWeight="400"
                fontSize="16px"
                lineHeight="26px"
              >
                Expired
              </Text>
            )}
          </GridItem>
          <GridItem justifySelf="end">
            <IconButton
              onClick={() => toggleOpen()}
              aria-label="expand"
              icon={<RxHamburgerMenu />}
              size="sm"
            />
          </GridItem>
          <AnimatePresence>
            {openDetails ? renderDetails() : null}
          </AnimatePresence>
        </Grid>
      ) : (
        <Grid
          width="100%"
          borderRadius="8px"
          border="1px solid"
          borderColor={isNFTAvailable ? 'gray.15' : 'error'}
          backgroundColor="black"
          justifyItems="center"
          padding="8px 16px"
          templateColumns="repeat(5, 1fr) 1.5fr 0.5fr 1fr"
          alignItems="center"
          gap={1}
        >
          <GridItem>
            <LogoSection vault={vault} tokenDetails={nftPool?.lpToken} />
          </GridItem>
          <GridItem>
            <VStack alignItems="flex-start" gap={1}>
              <SymbolsSection vault={vault} tokenDetails={nftPool?.lpToken} />
              <Text fontSize="14px" color="gray.4">
                ID: {tokenId}
              </Text>
            </VStack>
          </GridItem>
          <GridItem>
            <StrategySection vault={vault} />
          </GridItem>
          <GridItem>
            <HStack gap="8px">
              <NetworkIcon
                w="32px"
                h="32px"
                name={Mode.name}
                iconUrl={getNetworkIconUrl(Mode.chainId).replace(
                  'ipfs://',
                  'https://ipfs.io/ipfs/'
                )}
                imageSize={{ width: '32px', height: '32px' }}
              />
              <TokenIcon symbol="Kim" width="32px" height="32px" />
            </HStack>
          </GridItem>
          <GridItem>
            <PropertiesSection
              lockDetails={lockDetails}
              poolAddress={nftPool.id}
              tokenId={tokenId}
              isStakedToNitro={isStakedToNitro}
            />
          </GridItem>
          <GridItem justifySelf="flex-start">
            {isNFTAvailable && token ? (
              <SellPrice listing={listing} currency={token} />
            ) : (
              <Text
                color="error"
                fontWeight="400"
                fontSize="16px"
                lineHeight="26px"
              >
                Expired
              </Text>
            )}
          </GridItem>
          <GridItem>
            <IconButton
              onClick={() => toggleOpen()}
              aria-label="expand"
              icon={<RxHamburgerMenu />}
            />
          </GridItem>
          <GridItem>
            <RemoveItemButton id={listing.id} />
          </GridItem>
          <AnimatePresence>
            {openDetails ? renderDetails() : null}
          </AnimatePresence>
        </Grid>
      )}
    </>
  );
};
